import React, { useState, useEffect } from 'react';
import { FaGithub, FaLinkedin, FaDiscord } from 'react-icons/fa';
import clsx from 'clsx';
import axios from 'axios';
import { SpeedInsights } from "@vercel/speed-insights/react"
import { Analytics } from "@vercel/analytics/react"
import './App.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://backend-managment-backend.onrender.com/api';

// Auth context
const AuthContext = React.createContext(null);

function useAuth() {
  const [user, setUser] = useState(null);
  

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        setUser(null);
      }
    }
  }, []);
  

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API_BASE}/login`, { email, password });
      if (res.data && res.data.user) {
        const userData = res.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return true;
      }
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Login error:', error);
      throw error.response?.data?.error || 'Login failed';
    }
  };

  const register = async (email, password, name) => {
    try {
      const res = await axios.post(`${API_BASE}/register`, { email, password, name });
      if (res.data && res.data.user) {
        const userData = res.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return true;
      }
      throw new Error('Invalid response from server');
    } catch (error) {
      console.error('Register error:', error);
      throw error.response?.data?.error || 'Registration failed';
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return { user, login, register, logout };
}

function LoginForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const auth = React.useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      if (isLogin) {
        const success = await auth.login(email, password);
        if (!success) {
          setError('Invalid email or password');
        }
      } else {
        const success = await auth.register(email, password, name);
        if (!success) {
          setError('Registration failed. Please try again.');
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err.toString());
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-4">
      <div className="w-full max-w-md transform transition-all duration-300 scale-100 hover:scale-[1.01]">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 space-y-6 border border-white/20">
          {/* Logo or Brand Section */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-gray-500">
              {isLogin ? 'Sign in to manage your services' : 'Join us to get started'}
            </p>
          </div>

          {error && (
            <div className="p-4 text-sm text-red-500 bg-red-50 rounded-lg border border-red-100 flex items-center gap-2">
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="relative">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                <input
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white/50"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>

              {!isLogin && (
                <div className="relative">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Full Name</label>
                  <input
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white/50"
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
              )}

              <div className="relative">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
                <input
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white/50"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 text-white font-semibold rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transform transition-all duration-200 ${isLoading ? 'opacity-75 cursor-not-allowed' : 'hover:scale-[1.02] hover:shadow-lg'}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>{isLogin ? 'Signing in...' : 'Creating account...'}</span>
                </div>
              ) : (
                <span>{isLogin ? 'Sign In' : 'Create Account'}</span>
              )}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                {isLogin ? 'New to our platform?' : 'Already have an account?'}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="w-full py-2.5 text-purple-600 font-medium rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
            onClick={() => setIsLogin(!isLogin)}
          >
            {isLogin ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span>Create a new account</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>Sign in to your account</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ServiceInputs({ services, setServices }) {
  const handleChange = (key, value) => {
    setServices({ ...services, [key]: value });
  };
  const handleKeyChange = (oldKey, newKey) => {
    const { [oldKey]: val, ...rest } = services;
    setServices({ ...rest, [newKey]: val });
  };
  const handleRemove = (key) => {
    const { [key]: _, ...rest } = services;
    setServices(rest);
  };
  return (
    <div className="space-y-3">
      {Object.entries(services).map(([key, value], idx) => (
        <div key={key + idx} className="flex gap-3 items-center bg-white/50 p-3 rounded-lg backdrop-blur-sm">
          <input
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            value={key}
            onChange={e => handleKeyChange(key, e.target.value)}
            placeholder="Service Key"
          />
          <input
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            value={value}
            onChange={e => handleChange(key, e.target.value)}
            placeholder="Service URL"
          />
          <button
            type="button"
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg transition-colors"
            onClick={() => handleRemove(key)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        className="w-full py-2 text-purple-600 font-semibold rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
        onClick={() => setServices({ ...services, '': '' })}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add Service
      </button>
    </div>
  );
}

function ConfigList({ onSelect, onDelete }) {
  const [configs, setConfigs] = useState([]);
  const auth = React.useContext(AuthContext);

  useEffect(() => {
    if (auth.user) {
      axios.get(`${API_BASE}/configs`, {
        headers: { 'x-api-key': auth.user.apiKey }
      })
        .then(res => setConfigs(res.data))
        .catch(console.error);
    }
  }, [auth.user]);

  return (
    <div className="mt-8">
      <h2 className="text-xl font-semibold mb-4">Your Configurations</h2>
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {configs.map(config => (
          <div key={config._id} className="bg-white p-4 rounded shadow-md">
            <h3 className="font-bold">{config.appName}</h3>
            <p className="text-gray-600">{config.environment}</p>
            <p className="text-sm text-gray-500 mt-2">
              {Object.keys(config.services).length} services configured
            </p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => onSelect(config)}
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(config.appName, config.environment)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigForm() {
  const [appName, setAppName] = useState('');
  const [environment, setEnvironment] = useState('');
  const [services, setServices] = useState({});
  const [status, setStatus] = useState('');
  const [copied, setCopied] = useState(false);
  const auth = React.useContext(AuthContext);

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(auth.user?.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [editingId, setEditingId] = useState(null);

  const handleDocsClick = () => {
    window.open('https://github.com/Hjgaming/dynamic-url-managment-docs', '_blank');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setStatus('');
    try {
      if (editingId) {
        // Update existing config
        await axios.put(
          `${API_BASE}/configs/${appName}/${environment}`,
          { appName, environment, services },
          { headers: { 'x-api-key': auth.user.apiKey } }
        );
      } else {
        // Create new config
        await axios.post(
          `${API_BASE}/configs`,
          { appName, environment, services },
          { headers: { 'x-api-key': auth.user.apiKey } }
        );
      }
      setStatus('Saved!');
      setAppName('');
      setEnvironment('');
      setServices({});
      setEditingId(null);
    } catch (err) {
      console.error('Save error:', err);
      setStatus(err.response?.data?.error || 'Error saving');
    }
  };

  const handleDelete = async (appName, environment) => {
    setStatus('');
    try {
      await axios.delete(
        `${API_BASE}/config/${appName}/${environment}`,
        { headers: { 'x-api-key': auth.user.apiKey } }
      );
      setStatus('Deleted!');
    } catch (err) {
      console.error('Delete error:', err);
      setStatus(err.response?.data?.error || 'Error deleting');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="bg-white/90 backdrop-blur-lg p-6 rounded-xl shadow-lg">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Backend URL Config
              </h1>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto">
              <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center gap-3 flex-1 sm:flex-none">
                <div className="flex-1">
                  <div className="text-xs text-gray-500 mb-1">Your API Key</div>
                  <code className="font-mono text-sm text-gray-800 break-all">
                    {auth.user?.apiKey}
                  </code>
                </div>
                <button
                  onClick={handleCopyApiKey}
                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200 flex-shrink-0"
                  title="Copy API Key"
                >
                  {copied ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  )}
                </button>
              </div>

              <button
                onClick={auth.logout}
                className="px-6 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-2 justify-center whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Configuration Form */}
        <form className="bg-white/90 backdrop-blur-lg p-6 rounded-xl shadow-lg space-y-6" onSubmit={handleSave}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Configuration Details
            </h2>

            <button
              onClick={handleDocsClick}
              className="px-4 py-2 text-indigo-600 font-medium rounded-lg border-2 border-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-2 whitespace-nowrap"
              title="View Documentation"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <span>Docs</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">App Name</label>
              <input
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                value={appName}
                onChange={e => setAppName(e.target.value)}
                placeholder="Enter app name"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Environment</label>
              <input
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                value={environment}
                onChange={e => setEnvironment(e.target.value)}
                placeholder="e.g., production, staging"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Services</label>
            <ServiceInputs services={services} setServices={setServices} />
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              className="px-8 py-3 text-white font-semibold rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transform hover:scale-[1.02] transition-all duration-200 flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              {editingId ? 'Update' : 'Save'} Configuration
            </button>
          </div>

          {status && (
            <div className={`text-center text-sm p-3 rounded-lg ${status.includes('Error') ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
              {status}
            </div>
          )}
        </form>

        {/* Configurations List */}
        <ConfigList
          onSelect={config => {
            setAppName(config.appName);
            setEnvironment(config.environment);
            setServices(config.services);
            setEditingId(config._id);
          }}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
}

function LandingPage({ onLoginClick, onRegisterClick }) {
  const iconClass = clsx(
    'w-6 h-6',
    'transition-colors',
    'text-gray-400 hover:text-white'
  );
  const [stats, setStats] = useState({ users: 0, urls: 0 });

  useEffect(() => {
    // Fetch statistics from your API
    axios.get(`${API_BASE}/stats`)
      .then(res => setStats(res.data))
      .catch(console.error);
  }, []);

  const handleDocsClick = () => {
    window.open('https://github.com/Hjgaming/dynamic-url-managment-docs', '_blank');
  };
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex flex-col justify-between">
      <nav className="bg-white/90 backdrop-blur-lg shadow-lg sticky top-0 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent hover:scale-105 transition-transform cursor-pointer">
                Backend URL Manager
              </h1>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <button
                onClick={handleDocsClick}
                className="group px-4 py-2 text-gray-600 font-medium hover:text-indigo-600 transition-all flex items-center gap-2 hover:scale-105"
              >
                <svg className="w-5 h-5 group-hover:stroke-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span>Docs</span>
              </button>
              <button
                onClick={onLoginClick}
                className="px-6 py-2 text-indigo-600 font-medium rounded-lg border-2 border-indigo-600 hover:bg-indigo-50 transition-all hover:scale-105 hover:shadow-lg"
              >
                Login
              </button>
              <button
                onClick={onRegisterClick}
                className="px-6 py-2 text-white font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all hover:scale-105 shadow-md hover:shadow-xl"
              >
                Register
              </button>
            </div>
            <div className="md:hidden flex items-center">
              <button 
                onClick={toggleMobileMenu}
                className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 transition-colors"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle menu"
              >
                <svg 
                  className={`h-6 w-6 transition-transform duration-200 ${isMobileMenuOpen ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <div 
            className={`md:hidden transition-all duration-300 ease-in-out transform ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'} absolute left-0 right-0 bg-white/95 backdrop-blur-lg shadow-lg rounded-b-2xl overflow-hidden`}
            style={{ top: '64px' }}
          >
            <div className="px-4 pt-2 pb-3 space-y-2">
              <button
                onClick={handleDocsClick}
                className="w-full text-left px-4 py-3 text-gray-600 font-medium hover:text-indigo-600 transition-all flex items-center gap-2 hover:bg-gray-50/80 rounded-xl group"
              >
                <svg className="w-5 h-5 group-hover:stroke-indigo-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span>Documentation</span>
              </button>
              <button
                onClick={onLoginClick}
                className="w-full text-left px-4 py-3 text-indigo-600 font-medium hover:bg-indigo-50/80 transition-all rounded-xl flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>Login</span>
              </button>
              <button
                onClick={onRegisterClick}
                className="w-full text-left px-4 py-3 text-white font-medium bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all rounded-xl flex items-center gap-2 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span>Register</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-grow">
        {/* Hero Section - Enhanced with animations and better spacing */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center space-y-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 animate-fade-in leading-tight">
              Manage Your Backend URLs
              <br className="hidden sm:block" />
              with Ease
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up leading-relaxed">
              Simplify your backend configuration management with our powerful URL management system.
              Perfect for developers managing multiple environments and services.
            </p>
            <button
              onClick={onLoginClick}
              className="mt-8 px-8 py-4 text-white font-semibold rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl animate-bounce-subtle"
            >
              Get Started for Free
            </button>
          </div>
        </div>

        {/* Statistics Section - Enhanced with better animations and hover effects */}
        <div className="bg-white/80 backdrop-blur-lg py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center p-8 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                <div className="text-4xl font-bold text-indigo-600 mb-2 animate-count-up">{stats.totalUsers}</div>
                <div className="text-gray-600 font-medium">Active Users</div>
              </div>
              <div className="text-center p-8 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                <div className="text-4xl font-bold text-purple-600 mb-2 animate-count-up">{stats.totalConfigs}</div>
                <div className="text-gray-600 font-medium">URLs Managed</div>
              </div>
            </div>
          </div>
        </div>

        {/* About Section - Enhanced with better card effects */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">About Us</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're passionate about making backend configuration management easier for developers.
              Our platform helps you manage and organize your service URLs across different environments efficiently.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group p-6 rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-gradient-to-br hover:from-indigo-50 hover:to-purple-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">Easy to Use</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">Simple and intuitive interface for managing your backend configurations.</p>
            </div>
            <div className="group p-6 rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">Secure</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">Your configurations are encrypted and securely stored.</p>
            </div>
            <div className="group p-6 rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-gradient-to-br hover:from-pink-50 hover:to-indigo-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-pink-600 transition-colors">Scalable</h3>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors">Grows with your needs, from small projects to enterprise solutions.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer - Enhanced with better hover effects and spacing */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Backend URL Manager</h3>
              <p className="text-gray-400">Simplifying backend configuration management for developers worldwide.</p>
              <div className="flex space-x-4">
                {/* GitHub */}
                <a
                  href="https://github.com/hjgaming"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors"
                >
                  <FaGithub className={iconClass} />
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/in/harshal-jariwala-1101b2309"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors"
                >
                  <FaLinkedin className={iconClass} />
                </a>

                {/* Discord */}
                <a
                  href="https://discord.com/invite/cBM7e87KYj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors"
                >
                  <FaDiscord className={iconClass} />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Support</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Partners</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Backend URL Manager. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  const auth = useAuth();
  const [showLogin, setShowLogin] = useState(true);

  return (
    <AuthContext.Provider value={auth}>
      {!auth.user ? (
        window.location.pathname === '/' ? (
          <LandingPage
            onLoginClick={() => window.location.href = '/login'}
            onRegisterClick={() => window.location.href = '/register'}
          />
        ) : (
          <LoginForm isLogin={showLogin} />
        )
      ) : (
        <><ConfigForm /><SpeedInsights /><Analytics/></>
      )}
    </AuthContext.Provider>
  );
}

export default App;
